
import React, { useMemo, useState } from "react";
import { useLeads } from "@/context/LeadContext";
import { usePartners } from "@/context/PartnerContext";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
  XAxis,
  YAxis,
} from "recharts";
import {
  Users,
  PhoneCall,
  CheckCircle,
  Clock,
  TrendingUp,
  Percent,
  Target,
  LayoutDashboard,
  Calendar,
  Zap,
  AlertTriangle,
  MessageSquare,
  ArrowRight,
  UserPlus,
  ChevronUp,
  ChevronDown,
  Globe,
  Megaphone,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import StatisticsCard from "@/components/dashboard/StatisticsCard";
import { PageHeader } from "@/components/layouts/PageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { addMonths, format, formatDistanceToNow, isSameMonth, parseISO, subMonths } from "date-fns";
import { el } from "date-fns/locale";

// ── Stage display map ───────────────────────────────────────────────────────
const STAGE_LABELS: Record<string, { label: string; color: string }> = {
  "σεΕκκρεμότητα":       { label: "Σε Εκκρεμότητα", color: "bg-amber-100 text-amber-800" },
  "έκλεισεΗΔουλειά":    { label: "Επιτυχία",         color: "bg-emerald-100 text-emerald-800" },
  "ήθελεΠληροφορίες":   { label: "Πληροφορίες",      color: "bg-indigo-100 text-indigo-800" },
  "δενΑπάντησε":        { label: "Δεν Απάντησε",     color: "bg-slate-100 text-slate-700" },
  "αδύνατηΕπικοινωνία": { label: "Αδύνατη Επικ.",    color: "bg-rose-100 text-rose-800" },
};

// ── Avatar initials helper ──────────────────────────────────────────────────
function initials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const AVATAR_GRADIENTS = [
  "from-blue-500 to-indigo-600",
  "from-purple-500 to-pink-600",
  "from-emerald-500 to-teal-600",
  "from-orange-500 to-rose-600",
  "from-cyan-500 to-blue-600",
  "from-violet-500 to-purple-600",
];

// ── Component ───────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { leads } = useLeads();
  const { partners } = usePartners();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState("3m");

  // ── Statistics ────────────────────────────────────────────────────────────
  const getDateRange = () => {
    const now = new Date();
    const months = timeRange === "1y" ? 12 : timeRange === "6m" ? 6 : 3;
    return { startDate: subMonths(now, months), endDate: now };
  };

  const { startDate, endDate } = getDateRange();

  const filteredLeads = useMemo(
    () =>
      leads.filter((lead) => {
        const d = parseISO(lead.createdAt);
        return d >= startDate && d <= endDate;
      }),
    [leads, timeRange]
  );

  const filteredStats = useMemo(() => {
    const total   = filteredLeads.length;
    const success = filteredLeads.filter((l) => l.stage === "έκλεισεΗΔουλειά").length;
    const pending = filteredLeads.filter((l) => l.stage === "σεΕκκρεμότητα").length;
    const info    = filteredLeads.filter((l) => l.stage === "ήθελεΠληροφορίες").length;
    const conversion = total > 0 ? Math.round((success / total) * 100) : 0;
    return { total, success, pending, info, conversion };
  }, [filteredLeads]);

  const weeklyPulse = useMemo(() => {
    const now = new Date();
    const currentStart  = new Date(now.getTime() - 7  * 86400_000);
    const previousStart = new Date(now.getTime() - 14 * 86400_000);
    const current  = leads.filter((l) => { const d = parseISO(l.createdAt); return d >= currentStart; }).length;
    const previous = leads.filter((l) => { const d = parseISO(l.createdAt); return d >= previousStart && d < currentStart; }).length;
    const delta    = previous > 0 ? Math.round(((current - previous) / previous) * 100) : 0;
    return { current, previous, delta };
  }, [leads]);

  const actionableAlerts = useMemo(() => {
    const stalePending = filteredLeads.filter((l) => {
      if (l.stage !== "σεΕκκρεμότητα") return false;
      const ageHours = (Date.now() - parseISO(l.createdAt).getTime()) / 3_600_000;
      return ageHours >= 48;
    }).length;
    const failedSms = filteredLeads.filter((l) => l.smsStatus === "failed").length;
    const noPartner = filteredLeads.filter((l) => !l.partnerId).length;
    return { stalePending, failedSms, noPartner };
  }, [filteredLeads]);

  const totalAlerts = actionableAlerts.stalePending + actionableAlerts.failedSms + actionableAlerts.noPartner;

  // ── Chart data ────────────────────────────────────────────────────────────
  const serviceChartData = [
    { name: "Εξοικονομώ",      count: filteredLeads.filter((l) => l.service === "εξοικονομώ").length,            fill: "#3b82f6" },
    { name: "Αντλίες",         count: filteredLeads.filter((l) => l.service === "αντλίεςΘερμότητας").length,    fill: "#8b5cf6" },
    { name: "Φωτοβολταϊκά",   count: filteredLeads.filter((l) => l.service === "φωτοβολταϊκά").length,          fill: "#10b981" },
    { name: "Κλιματιστικά",   count: filteredLeads.filter((l) => l.service === "κλιματιστικά").length,          fill: "#f59e0b" },
    { name: "Φυσικό Αέριο",   count: filteredLeads.filter((l) => l.service === "φυσικόΑέριο").length,           fill: "#ef4444" },
    { name: "Ηλιακά",         count: filteredLeads.filter((l) => l.service === "ηλιακά").length,                fill: "#14b8a6" },
    { name: "Άλλο",           count: filteredLeads.filter((l) => l.service === "άλλο").length,                  fill: "#6b7280" },
  ];

  const stageData = [
    { name: "Σε εκκρεμότητα", value: filteredLeads.filter((l) => l.stage === "σεΕκκρεμότητα").length,    color: "#f59e0b" },
    { name: "Επιτυχία",        value: filteredLeads.filter((l) => l.stage === "έκλεισεΗΔουλειά").length,  color: "#10b981" },
    { name: "Πληροφορίες",     value: filteredLeads.filter((l) => l.stage === "ήθελεΠληροφορίες").length, color: "#6366f1" },
  ];

  const monthlyTrendData = useMemo(() => {
    const months: Date[] = [];
    let cur = new Date(startDate);
    while (cur <= endDate) { months.push(new Date(cur)); cur = addMonths(cur, 1); }
    return months.map((m) => {
      const ml = leads.filter((l) => isSameMonth(parseISO(l.createdAt), m));
      return {
        name:    format(m, "MMM", { locale: el }),
        total:   ml.length,
        success: ml.filter((l) => l.stage === "έκλεισεΗΔουλειά").length,
      };
    });
  }, [leads, timeRange]);

  const partnerPerformance = useMemo(
    () =>
      partners
        .map((p) => {
          const pl = filteredLeads.filter((l) => l.partnerId === p.id);
          const ok = pl.filter((l) => l.stage === "έκλεισεΗΔουλειά").length;
          return {
            name:    p.name.length > 15 ? p.name.slice(0, 15) + "…" : p.name,
            leads:   pl.length,
            success: ok,
            rate:    pl.length > 0 ? Math.round((ok / pl.length) * 100) : 0,
          };
        })
        .sort((a, b) => b.leads - a.leads)
        .slice(0, 5),
    [partners, filteredLeads]
  );

  // ── Source & Campaign breakdown ───────────────────────────────────────────
  const sourceStats = useMemo(() => {
    const sheets = filteredLeads.filter(l => l.source === 'facebook_sheets').length;
    const ads    = filteredLeads.filter(l => l.source === 'facebook_leads').length;
    const web    = filteredLeads.filter(l => !l.source || l.source === 'web').length;
    return [
      { label: 'Facebook Sheets', count: sheets, color: 'bg-blue-500',   text: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-100' },
      { label: 'Facebook Ads',    count: ads,    color: 'bg-indigo-500',  text: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-100' },
      { label: 'Web Form',        count: web,    color: 'bg-slate-400',   text: 'text-slate-700',  bg: 'bg-slate-50',  border: 'border-slate-100' },
    ];
  }, [filteredLeads]);

  const campaignStats = useMemo(() => {
    const map: Record<string, number> = {};
    filteredLeads.forEach(l => {
      if (l.campaign) map[l.campaign] = (map[l.campaign] ?? 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [filteredLeads]);

  // ── Recent leads feed ─────────────────────────────────────────────────────
  const recentLeads = useMemo(
    () =>
      [...leads]
        .sort((a, b) => parseISO(b.createdAt).getTime() - parseISO(a.createdAt).getTime())
        .slice(0, 6),
    [leads]
  );

  const partnerMap = useMemo(
    () => Object.fromEntries(partners.map((p) => [p.id, p.name])),
    [partners]
  );

  // ── Quick actions config ──────────────────────────────────────────────────
  const quickActions = [
    { label: "Leads",         desc: "Διαχείριση επαφών",     icon: PhoneCall,     path: "/admin/leads",             color: "text-blue-600",   bg: "bg-blue-50" },
    { label: "Μηνύματα",     desc: "SMS & Email log",         icon: MessageSquare, path: "/admin/messages",          color: "text-violet-600", bg: "bg-violet-50" },
    { label: "Αναφορές",     desc: "Στατιστικά & γραφήματα", icon: TrendingUp,    path: "/admin/reports",           color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Ανάθεση",      desc: "Αυτόματη ανάθεση leads", icon: Users,         path: "/admin/leads/assignment",  color: "text-orange-600", bg: "bg-orange-50" },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <PageHeader
        title="Πίνακας Ελέγχου"
        description="Επισκόπηση απόδοσης και leads"
        icon={<LayoutDashboard />}
        iconGradient="blue"
        actions={
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-48 bg-white border-slate-200">
              <Calendar className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Περίοδος" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3m">Τελευταίοι 3 μήνες</SelectItem>
              <SelectItem value="6m">Τελευταίοι 6 μήνες</SelectItem>
              <SelectItem value="1y">Τελευταίος χρόνος</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      {/* Weekly Pulse Banner */}
      <div className="flex items-center gap-4 rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-3.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white shadow">
          <Zap className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800">
            Εβδομαδιαίος Παλμός
          </p>
          <p className="text-xs text-slate-500">
            {weeklyPulse.current} νέα leads αυτή την εβδομάδα
            {weeklyPulse.previous > 0 && (
              <span className={`ml-2 font-medium ${weeklyPulse.delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                {weeklyPulse.delta >= 0 ? "↑" : "↓"} {Math.abs(weeklyPulse.delta)}% vs προηγ.
              </span>
            )}
          </p>
        </div>
        {totalAlerts > 0 && (
          <Badge className="shrink-0 bg-rose-600 text-white hover:bg-rose-700">
            {totalAlerts} alerts
          </Badge>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatisticsCard
          title="Συνολικές Επαφές"
          value={filteredStats.total}
          description="Στην επιλεγμένη περίοδο"
          icon={<PhoneCall className="h-5 w-5" />}
          variant="info"
          trend={weeklyPulse.previous > 0 ? { value: weeklyPulse.delta, isPositive: weeklyPulse.delta >= 0 } : undefined}
        />
        <StatisticsCard
          title="Συνεργάτες"
          value={partners.length}
          description="Ενεργοί συνεργάτες"
          icon={<Users className="h-5 w-5" />}
          variant="default"
        />
        <StatisticsCard
          title="Επιτυχημένες"
          value={filteredStats.success}
          description="Ολοκληρωμένες"
          icon={<CheckCircle className="h-5 w-5" />}
          variant="success"
        />
        <StatisticsCard
          title="Σε Αναμονή"
          value={filteredStats.pending}
          description="Σε εκκρεμότητα"
          icon={<Clock className="h-5 w-5" />}
          variant="warning"
        />
        <StatisticsCard
          title="Μετατροπή"
          value={`${filteredStats.conversion}%`}
          description="Ποσοστό επιτυχίας"
          icon={<Percent className="h-5 w-5" />}
          variant={filteredStats.conversion > 50 ? "success" : "default"}
        />
      </div>

      {/* Sources & Campaigns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sources */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-50">
                <Globe className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Leads ανά Πηγή</CardTitle>
                <CardDescription>Από πού έρχονται οι επαφές</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {sourceStats.map(({ label, count, color, text, bg, border }) => (
              <div key={label} className={`flex items-center justify-between rounded-lg border p-3 ${bg} ${border}`}>
                <div className="flex items-center gap-2.5">
                  <div className={`h-2.5 w-2.5 rounded-full ${color}`} />
                  <span className={`text-sm font-medium ${text}`}>{label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${text}`}>{count}</span>
                  {filteredStats.total > 0 && (
                    <span className="text-xs text-slate-400">
                      {Math.round((count / filteredStats.total) * 100)}%
                    </span>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Campaigns */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-violet-50">
                <Megaphone className="h-4 w-4 text-violet-600" />
              </div>
              <div>
                <CardTitle className="text-base">Leads ανά Καμπάνια</CardTitle>
                <CardDescription>Κορυφαίες καμπάνιες στην περίοδο</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {campaignStats.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Δεν υπάρχουν δεδομένα καμπάνιας ακόμα</p>
            ) : (
              <div className="space-y-2.5">
                {campaignStats.map(([name, count]) => (
                  <div key={name}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600 truncate max-w-[70%]" title={name}>{name}</span>
                      <span className="font-semibold text-slate-800 shrink-0">{count}</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-violet-500 transition-all duration-500"
                        style={{ width: `${filteredStats.total > 0 ? Math.round((count / filteredStats.total) * 100) : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick actions — icon card grid */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Γρήγορες Ενέργειες</CardTitle>
            <CardDescription>Συχνές ενέργειες διαχείρισης</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {quickActions.map(({ label, desc, icon: Icon, path, color, bg }) => (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="group flex flex-col items-center gap-2.5 rounded-xl border border-slate-100 bg-white p-4 text-center shadow-sm transition-all hover:border-slate-200 hover:shadow-md active:scale-[0.97]"
                >
                  <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${bg} transition-transform group-hover:scale-110`}>
                    <Icon className={`h-5 w-5 ${color}`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{label}</p>
                    <p className="text-xs text-slate-400 leading-tight mt-0.5">{desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Alerts panel */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Alerts
              {totalAlerts > 0 && (
                <Badge className="ml-auto bg-rose-100 text-rose-700 hover:bg-rose-100">
                  {totalAlerts}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>Απαιτεί προσοχή</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2.5">
            <div
              className={`flex items-center justify-between rounded-lg border-l-4 p-3 ${
                actionableAlerts.noPartner > 0
                  ? "border-l-amber-400 bg-amber-50"
                  : "border-l-slate-200 bg-slate-50"
              }`}
            >
              <span className="text-sm text-slate-700">Μη ανατεθειμένα</span>
              <Badge variant={actionableAlerts.noPartner > 0 ? "secondary" : "outline"}>
                {actionableAlerts.noPartner}
              </Badge>
            </div>
            <div
              className={`flex items-center justify-between rounded-lg border-l-4 p-3 ${
                actionableAlerts.stalePending > 0
                  ? "border-l-orange-400 bg-orange-50"
                  : "border-l-slate-200 bg-slate-50"
              }`}
            >
              <span className="text-sm text-slate-700">Pending &gt; 48h</span>
              <Badge variant={actionableAlerts.stalePending > 0 ? "secondary" : "outline"}>
                {actionableAlerts.stalePending}
              </Badge>
            </div>
            <div
              className={`flex items-center justify-between rounded-lg border-l-4 p-3 ${
                actionableAlerts.failedSms > 0
                  ? "border-l-rose-500 bg-rose-50"
                  : "border-l-slate-200 bg-slate-50"
              }`}
            >
              <span className="text-sm text-slate-700">SMS αποτυχίες</span>
              <Badge variant={actionableAlerts.failedSms > 0 ? "destructive" : "outline"}>
                {actionableAlerts.failedSms}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trend chart + Pipeline (3-col: chart 2 cols, pipeline 1 col) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend chart */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-50">
                <TrendingUp className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Τάση Επαφών</CardTitle>
                <CardDescription>Μηνιαία εξέλιξη leads</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyTrendData}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.08)",
                  }}
                />
                <Legend />
                <Area type="monotone" dataKey="total"   name="Επαφές"    stroke="#3b82f6" fillOpacity={1} fill="url(#colorTotal)" />
                <Area type="monotone" dataKey="success" name="Επιτυχίες" stroke="#10b981" fillOpacity={1} fill="url(#colorSuccess)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Pipeline snapshot */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-rose-50">
                <ArrowRight className="h-4 w-4 text-rose-600" />
              </div>
              <div>
                <CardTitle className="text-base">Pipeline</CardTitle>
                <CardDescription>Ροή leads</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {[
              {
                label: "Επιτυχημένες",
                count: filteredStats.success,
                pct: filteredStats.conversion,
                bar: "bg-emerald-500",
              },
              {
                label: "Σε Εκκρεμότητα",
                count: filteredStats.pending,
                pct: filteredStats.total > 0 ? Math.round((filteredStats.pending / filteredStats.total) * 100) : 0,
                bar: "bg-amber-500",
              },
              {
                label: "Πληροφορίες",
                count: filteredStats.info,
                pct: filteredStats.total > 0 ? Math.round((filteredStats.info / filteredStats.total) * 100) : 0,
                bar: "bg-indigo-500",
              },
            ].map(({ label, count, pct, bar }) => (
              <div key={label}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-slate-600">{label}</span>
                  <span className="font-semibold text-slate-800">
                    {count} <span className="text-slate-400 font-normal">/ {filteredStats.total}</span>
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full ${bar} transition-all duration-500`}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-0.5 text-right">{pct}%</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Services + Partners charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Services */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-amber-50">
                <Zap className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-base">Leads ανά Υπηρεσία</CardTitle>
                <CardDescription>Κατανομή ενδιαφέροντος</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={serviceChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal vertical={false} />
                <XAxis type="number"   tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" width={95} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                <Bar dataKey="count" name="Leads" radius={[0, 4, 4, 0]}>
                  {serviceChartData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top partners */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-50">
                <Users className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-base">Top Συνεργάτες</CardTitle>
                <CardDescription>Κορυφαίοι 5 σε leads</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={partnerPerformance} layout="vertical" margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal vertical={false} />
                <XAxis type="number"   tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                <Legend />
                <Bar dataKey="leads"   name="Leads"      fill="#3b82f6" radius={[0, 4, 4, 0]} />
                <Bar dataKey="success" name="Επιτυχίες"  fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Leads feed */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-slate-100">
              <UserPlus className="h-4 w-4 text-slate-600" />
            </div>
            <div>
              <CardTitle className="text-base">Πρόσφατα Leads</CardTitle>
              <CardDescription>Τελευταίες 6 εισερχόμενες επαφές</CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/leads")} className="text-blue-600 hover:text-blue-700 gap-1">
            Όλα <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </CardHeader>
        <CardContent>
          {recentLeads.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Δεν υπάρχουν leads ακόμα</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentLeads.map((lead, idx) => {
                const stageMeta = STAGE_LABELS[lead.stage] ?? { label: lead.stage, color: "bg-slate-100 text-slate-700" };
                const gradClass = AVATAR_GRADIENTS[idx % AVATAR_GRADIENTS.length];
                const partnerName = lead.partnerId ? partnerMap[lead.partnerId] : null;

                return (
                  <button
                    key={lead.id}
                    onClick={() => navigate(`/admin/leads/${lead.id}`)}
                    className="w-full flex items-center gap-3 py-3 text-left hover:bg-slate-50 transition-colors rounded-lg px-2 -mx-2 group"
                  >
                    {/* Avatar */}
                    <div
                      className={`h-9 w-9 shrink-0 rounded-full bg-gradient-to-br ${gradClass} flex items-center justify-center text-white text-xs font-bold shadow-sm`}
                    >
                      {initials(lead.name || "?")}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-blue-700 transition-colors">
                        {lead.name}
                      </p>
                      <p className="text-xs text-slate-400 truncate">
                        {lead.service}
                        {partnerName && <span className="ml-1.5">· {partnerName}</span>}
                      </p>
                    </div>

                    {/* Stage badge */}
                    <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${stageMeta.color}`}>
                      {stageMeta.label}
                    </span>

                    {/* Time */}
                    <span className="shrink-0 text-xs text-slate-400 hidden sm:block">
                      {formatDistanceToNow(parseISO(lead.createdAt), { addSuffix: true, locale: el })}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
